<script lang="ts" setup>
import {
    faChevronLeft,
    faSpinnerThird,
} from "@fortawesome/pro-regular-svg-icons";
import { faEdit } from "@fortawesome/pro-solid-svg-icons";
import { AppPage } from "#components";
import type { RouteLocationRaw } from "vue-router";

interface AppPageBreadCrumb {
    label?: string | number;
    to?: string | RouteLocationRaw;
}

interface AppListPageProps {
    label?: string;
    title?: string;
    is?: any;
    containerClass?: string;
    topClass?: string;
    wrapperClass?: string;
    editable?: boolean;
    titleClass?: string | any[] | object;
    breadcrumbs?: AppPageBreadCrumb[];
    loading?: boolean;
    regular?: boolean;
}

const props = withDefaults(defineProps<AppListPageProps>(), {
    breadcrumbs: () => [],
    label: undefined,
    title: undefined,
});

const emit = defineEmits<{
    (e: "edit"): void;
}>();

const returns = computed(() =>
    [...props.breadcrumbs].reverse().find((crumb) => crumb.to || crumb.click),
);
</script>

<template>
    <component :is="is ?? AppPage" :title="title">
        <div
            v-if="loading"
            class="w-full flex h-full bg-gray-100 items-center justify-center pt-3"
        >
            <FontAwesomeIcon
                :icon="faSpinnerThird"
                class="text-primary-500"
                size="2x"
                spin
            />
        </div>
        <template v-else>
            <div
                class="flex flex-col md:flex-row items-start gap-4 md:items-end w-full"
                :class="topClass ? topClass : 'p-8'"
            >
                <div class="flex gap-4 items-center">
                    <UButton
                        v-if="returns"
                        :to="returns.to"
                        class="btn btn-sm normal-case flex items-center justify-center w-9 h-9"
                        color="gray"
                        size="sm"
                        square
                        @click="returns.click"
                    >
                        <FontAwesomeIcon
                            :icon="faChevronLeft"
                            class="text-base"
                        />
                    </UButton>
                    <UButton
                        v-if="editable"
                        :padded="false"
                        class="gap-2 group"
                        color="black"
                        variant="ghost"
                        @click="emit('edit')"
                    >
                        <slot name="pre-title" />
                        <template v-if="$slots.title">
                            <slot name="title" />
                        </template>
                        <h1
                            v-else
                            :class="titleClass"
                            class="relative text-2xl font-medium truncate font-display flex flex-col items-start gap-0"
                        >
                            <span
                                v-if="label"
                                class="text-xs opacity-50 -mb-1.5"
                                >{{ label }}</span
                            >
                            <span>
                                <slot name="title-text">{{ title }}</slot>
                            </span>
                        </h1>
                        <FontAwesomeIcon
                            :icon="faEdit"
                            class="text-lg opacity-0 group-hover:opacity-75 group-active:opacity-100"
                        />
                        <slot name="post-title" />
                    </UButton>
                    <template v-else>
                        <slot name="pre-title" />
                        <div
                            v-if="$slots.title"
                            :class="titleClass"
                            class="relative font-medium font-display flex flex-col items-start gap-0"
                        >
                            <span
                                v-if="label"
                                @click="
                                    returns
                                        ? returns.click
                                            ? returns.click()
                                            : navigateTo(returns.to)
                                        : undefined
                                "
                                class="text-xs -mt-4 opacity-50"
                                :class="{
                                    'hover:underline cursor-pointer': returns,
                                }"
                                >{{ label }}</span
                            >
                            <slot name="title" />
                        </div>
                        <h1
                            v-else
                            class="relative text-2xl font-medium truncate font-display flex flex-col items-start gap-0"
                        >
                            <span
                                v-if="label"
                                class="text-xs opacity-50 -mb-1.5"
                                >{{ label }}</span
                            >
                            <slot name="title-text">{{ title }}</slot>
                        </h1>
                        <slot name="post-title" />
                    </template>
                </div>
                <div
                    class="h-full flex-1 flex items-center justify-between gap-4"
                >
                    <slot name="actions" />
                </div>
            </div>
            <div
                class="relative flex-1 flex overflow-auto divide-x divide-gray-300 w-full md:pl-8 bg-white"
                :class="wrapperClass"
            >
                <div
                    :class="[
                        {
                            'md:h-[calc(100%-2rem)] md:w-[calc(100%-4rem)] md:ml-8 [&>div:first-child]:rounded-l-xl [&>div:last-child]:rounded-r-xl [&>div]:border-y [&>div]:border-r [&>div:first-child]:border-l [&>div]:border-gray-300':
                                !regular,
                        },
                        containerClass,
                    ]"
                    class="h-full w-full flex absolute left-0 top-0 flex-grow-0"
                >
                    <slot />
                </div>
            </div>
        </template>
    </component>
</template>
